import { storage } from '../lib/firebase';
import { ref, uploadBytes, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db } from '../lib/firebase';
import { collection, addDoc, serverTimestamp, doc, getDoc } from 'firebase/firestore';

// Database structure: vindie/{collection}

// Function to get video duration from URL
async function getVideoDuration(videoUrl: string): Promise<number> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.preload = 'metadata';
    
    const timeout = setTimeout(() => {
      reject(new Error('Video duration timeout'));
    }, 10000); // 10 second timeout
    
    video.addEventListener('loadedmetadata', () => {
      clearTimeout(timeout);
      const duration = Math.round(video.duration);
      resolve(duration);
    });
    
    video.addEventListener('error', () => {
      clearTimeout(timeout);
      reject(new Error('Could not load video metadata'));
    });
    
    video.src = videoUrl;
  });
}

// Alternative upload service that bypasses CORS issues
export interface UploadPayload {
  title: string;
  description: string;
  category: string;
  visibility: string;
  rating?: string;
  language?: string;
  genres?: string[];
  tags?: string;
  linkedContent?: string;
  seasonNumber?: string;
  episodeNumber?: string;
  chapterNumber?: string;
  commentsEnabled?: boolean;
  ageRestriction?: boolean;
  donationRequired?: boolean;
}

export interface AssetUrls {
  [key: string]: string | string[];
}

// Direct upload with progress tracking for mobile devices
export async function uploadFileDirect(
  path: string, 
  file: File, 
  onProgress?: (progress: number) => void
): Promise<string> {
  try {
    // Create a unique filename to avoid conflicts
    const timestamp = Date.now();
    const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const fileName = `${timestamp}-${sanitizedName}`;

    const storageRef = ref(storage, `${path}/${fileName}`);

    // Use resumable upload for better progress tracking, especially on mobile
    const uploadTask = uploadBytesResumable(storageRef, file, {
      contentType: file.type || 'application/octet-stream',
      customMetadata: {
        originalName: file.name,
        uploadedAt: new Date().toISOString(),
        fileSize: file.size.toString(),
        uploadSource: 'vindie-stream-web-direct'
      }
    });

    // Track upload progress
    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          // Calculate progress percentage
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          if (onProgress) {
            onProgress(progress);
          }
        },
        (error) => {
          console.error('Upload error:', error);
          reject(error);
        },
        async () => {
          // Upload completed successfully
          try {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            if (onProgress) {
              onProgress(100);
            }
            resolve(url);
          } catch (error) {
            console.error('Error getting download URL:', error);
            reject(error);
          }
        }
      );
    });
  } catch (error) {
    console.error('Direct upload error:', error);
    throw error;
  }
}

// Create content document with direct upload
export async function createContentDocumentDirect(uid: string, payload: UploadPayload, assetUrls: AssetUrls) {
  const col = collection(db, 'content');
  
  // Fetch user's actual name from their profile
  let uploaderName = 'Creator';
  try {
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      uploaderName = userData.name || userData.displayName || 'Creator';
    }
  } catch (error) {
    console.error('Error fetching user profile:', error);
  }
  
  // Build document data, filtering out undefined values
  const docData: any = {
    ownerId: uid,
    uploaderId: uid, // Add this for content service compatibility
    uploader: uploaderName, // Use actual user's name
    ...payload,
    assets: assetUrls,
    views: 0,
    likes: 0,
    dislikes: 0,
    shares: 0,
    comments: 0,
    status: 'published',
    isPublic: payload.visibility === 'public',
    year: new Date().getFullYear(),
    slug: payload.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').substring(0, 100),
    tags: Array.isArray(payload.tags) ? payload.tags : (payload.tags ? payload.tags.split(',').map((tag: string) => tag.trim()).filter(Boolean) : []),
    // Add required fields for ContentItem interface
    thumbnail: assetUrls.poster || assetUrls.thumbnail || '',
    poster: assetUrls.poster || '',
    trailer: assetUrls.trailer || '',
    rating: 0.0, // Default rating as number
    genres: payload.genres || [],
    language: payload.language || 'English',
    contentRating: payload.rating || 'All Ages',
    // Content settings
    commentsEnabled: payload.commentsEnabled !== false, // Default to true
    ageRestriction: payload.ageRestriction === true,
    donationRequired: payload.donationRequired === true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  // Calculate actual video duration
  let duration = null;
  
  if (assetUrls) {
    // Find the main video file (not trailer)
    for (const [key, value] of Object.entries(assetUrls)) {
      if (typeof value === 'string' && value.includes('firebasestorage') && 
          !key.toLowerCase().includes('trailer') && 
          (value.toLowerCase().includes('.mp4') || value.toLowerCase().includes('.mov') || value.toLowerCase().includes('.avi'))) {
        try {
          duration = await getVideoDuration(value);
          break;
        } catch (error) {
          // Silently fail - will use fallback duration
        }
      }
    }
  }
  
  // Fallback to default duration if we couldn't calculate it
  if (duration === null) {
    duration = payload.category === 'movie' ? 120 : 
               payload.category === 'series' ? 45 : 
               payload.category === 'short' ? 60 : 
               null;
  }
  
  if (duration !== null) {
    docData.duration = duration;
  }

  // Filter out undefined values from payload
  Object.keys(docData).forEach(key => {
    if (docData[key] === undefined) {
      delete docData[key];
    }
  });

  const docRef = await addDoc(col, docData);
  return docRef.id;
}

    // Upload book with cover and book file
    export const uploadBookWithFiles = async (bookData: {
      title: string;
      description: string;
      author: string;
      genre: string;
      language: string;
      contentRating: string;
      tags: string[];
      coverFile?: File;
      bookFile?: File;
      uploaderId: string;
      uploaderName: string;
      contentType: 'book' | 'comic';
    }): Promise<string> => {
  try {
    let thumbnailUrl = '';
    let bookFileUrl = '';

    // Upload cover image if provided
    if (bookData.coverFile) {
      const coverRef = ref(storage, `book-covers/${bookData.uploaderId}/${Date.now()}_${bookData.coverFile.name}`);
      const coverSnapshot = await uploadBytes(coverRef, bookData.coverFile);
      thumbnailUrl = await getDownloadURL(coverSnapshot.ref);
    }

    // Upload book file if provided
    if (bookData.bookFile) {
      const bookRef = ref(storage, `books/${bookData.uploaderId}/${Date.now()}_${bookData.bookFile.name}`);
      const bookSnapshot = await uploadBytes(bookRef, bookData.bookFile);
      bookFileUrl = await getDownloadURL(bookSnapshot.ref);
    }

        // Save book metadata to Firestore
        const contentData = {
          title: bookData.title,
          description: bookData.description,
          category: bookData.contentType,
      thumbnail: thumbnailUrl,
      rating: 0,
      views: 0,
      likes: 0,
      dislikes: 0,
      uploader: bookData.uploaderName,
      uploaderId: bookData.uploaderId,
      genres: [bookData.genre],
      language: bookData.language,
      contentRating: bookData.contentRating,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      status: 'published' as const,
      isPublic: true,
      tags: bookData.tags,
      author: bookData.author,
      assets: {
        bookFile: bookFileUrl,
        coverFile: thumbnailUrl
      }
    };

    const docRef = await addDoc(collection(db, 'content'), contentData);
    return docRef.id;
  } catch (error) {
    console.error('Error uploading book:', error);
    throw error;
  }
};